/**
 * Panel admina > SFD B2B: dane logowania do portalu b2b.sfd.pl dla
 * "Kontroli faktur SFD". Haslo nigdy nie wraca z serwera - tylko `ma_haslo`;
 * puste pole hasla = "bez zmian". Kontrakt: scratchpad/kontrola/KONTRAKT.md.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { kontrolaFakturService } from '../../services/kontrolaFakturService';
import { formatujDateCzas } from '../kontrola-faktur/format';
import SfdWplywGmail from './SfdWplywGmail';

const komunikatBledu = (err, domyslny) => {
  switch (err?.errorCode) {
    case 'SFD_BRAK_KONFIGURACJI': return 'Brak e-maila lub hasła — zapisz najpierw dane logowania.';
    case 'SFD_LOGOWANIE': return 'Portal SFD odrzucił logowanie — sprawdź e-mail i hasło.';
    case 'SFD_PORTAL': return `Błąd portalu SFD: ${err.message || 'nieznany'}`;
    case 'SYNC_W_TOKU': return 'Trwa synchronizacja — spróbuj za chwilę.';
    // Poczta jako zapasowe zrodlo plikow (ANEKS 4)
    case 'MAIL_BRAK_KONFIGURACJI': return 'Brak danych skrzynki (serwer, login lub hasło) — uzupełnij i zapisz.';
    case 'MAIL_LOGOWANIE': return 'Serwer poczty odrzucił logowanie — sprawdź login i hasło (dla Gmaila: hasło aplikacji).';
    case 'MAIL_BLAD': return `Błąd poczty: ${err.message || 'nieznany'}`;
    default: return err?.message || domyslny;
  }
};

const input = { width: '100%', maxWidth: '420px', padding: '6px 8px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '4px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' };
const label = { display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' };
const btn = (tlo, kolor, ramka) => ({ padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', border: `1px solid ${ramka}`, background: tlo, color: kolor });

const SfdB2BSettings = () => {
  const [ustawienia, setUstawienia] = useState({
    wlaczone: false, email: '', ma_haslo: false, ostatnia_sync: null, sklepy: [], mapa_sklepow: [],
    // Poczta (ANEKS 4) - domyslne wartosci z kontraktu
    sfd_mail_enabled: false, sfd_mail_host: 'imap.gmail.com', sfd_mail_port: 993, sfd_mail_user: '',
    ma_haslo_mail: false, sfd_mail_folder: 'INBOX', sfd_mail_nadawcy: 'sfd.pl', sfd_mail_dni: 180,
  });
  const [haslo, setHaslo] = useState('');
  const [hasloMail, setHasloMail] = useState('');
  // Mapowanie adresow -> sklepy (ANEKS 1): {klucz: sklep_id|null}; edycja lokalna, zapis razem z formularzem
  const [mapa, setMapa] = useState({});
  const [ladowanie, setLadowanie] = useState(true);
  const [zapis, setZapis] = useState(false);
  const [test, setTest] = useState(false);
  const [testMail, setTestMail] = useState(false);
  const [komunikat, setKomunikat] = useState(null);

  useEffect(() => {
    let aktywny = true;
    (async () => {
      try {
        const dane = await kontrolaFakturService.ustawienia();
        if (aktywny && dane) {
          // Backend moze oddac klucze poczty plasko (sfd_mail_*) albo w obiekcie `mail`
          const poczta = dane.mail || {};
          setUstawienia(prev => ({
            ...prev,
            ...dane,
            sfd_mail_enabled: dane.sfd_mail_enabled ?? poczta.wlaczone ?? prev.sfd_mail_enabled,
            sfd_mail_host: dane.sfd_mail_host ?? poczta.host ?? prev.sfd_mail_host,
            sfd_mail_port: dane.sfd_mail_port ?? poczta.port ?? prev.sfd_mail_port,
            sfd_mail_user: dane.sfd_mail_user ?? poczta.user ?? prev.sfd_mail_user,
            ma_haslo_mail: dane.ma_haslo_mail ?? poczta.ma_haslo ?? false,
            sfd_mail_folder: dane.sfd_mail_folder ?? poczta.folder ?? prev.sfd_mail_folder,
            sfd_mail_nadawcy: dane.sfd_mail_nadawcy ?? poczta.nadawcy ?? prev.sfd_mail_nadawcy,
            sfd_mail_dni: dane.sfd_mail_dni ?? poczta.dni ?? prev.sfd_mail_dni,
          }));
          const m = {};
          (Array.isArray(dane.mapa_sklepow) ? dane.mapa_sklepow : []).forEach(w => { m[w.klucz] = w.sklep_id ?? null; });
          setMapa(m);
        }
      } catch (err) {
        if (aktywny && !err?.isAuthError && !err?.isPermissionError) {
          setKomunikat({ typ: 'blad', tekst: err?.message || 'Nie można pobrać ustawień' });
        }
      } finally {
        if (aktywny) setLadowanie(false);
      }
    })();
    return () => { aktywny = false; };
  }, []);

  const zapisz = async () => {
    setZapis(true);
    setKomunikat(null);
    try {
      await kontrolaFakturService.zapiszUstawienia({
        wlaczone: Boolean(ustawienia.wlaczone),
        email: (ustawienia.email || '').trim(),
        haslo: haslo ? haslo : null,
        mapa_sklepow: mapa,
        // Poczta (ANEKS 4); haslo puste = bez zmian
        sfd_mail_enabled: Boolean(ustawienia.sfd_mail_enabled),
        sfd_mail_host: (ustawienia.sfd_mail_host || '').trim(),
        sfd_mail_port: Number(ustawienia.sfd_mail_port) || 993,
        sfd_mail_user: (ustawienia.sfd_mail_user || '').trim(),
        sfd_mail_haslo: hasloMail ? hasloMail : null,
        sfd_mail_folder: (ustawienia.sfd_mail_folder || 'INBOX').trim(),
        sfd_mail_nadawcy: (ustawienia.sfd_mail_nadawcy || '').trim(),
        sfd_mail_dni: Number(ustawienia.sfd_mail_dni) || 180,
      });
      // po zapisie przypisania reczne staja sie 'mapa'
      setUstawienia(prev => ({
        ...prev,
        mapa_sklepow: (prev.mapa_sklepow || []).map(w => (
          mapa[w.klucz] !== undefined && mapa[w.klucz] !== (w.sklep_id ?? null)
            ? { ...w, sklep_id: mapa[w.klucz], zrodlo: mapa[w.klucz] === null ? null : 'mapa' }
            : w
        )),
      }));
      if (haslo) setUstawienia(prev => ({ ...prev, ma_haslo: true }));
      if (hasloMail) setUstawienia(prev => ({ ...prev, ma_haslo_mail: true }));
      setHaslo('');
      setHasloMail('');
      setKomunikat({ typ: 'ok', tekst: 'Zapisano ustawienia SFD B2B.' });
    } catch (err) {
      setKomunikat({ typ: 'blad', tekst: komunikatBledu(err, 'Nie udało się zapisać ustawień') });
    } finally {
      setZapis(false);
    }
  };

  const testuj = async () => {
    setTest(true);
    setKomunikat(null);
    try {
      const wynik = await kontrolaFakturService.testujLogowanie();
      if (wynik?.ok) {
        setKomunikat({ typ: 'ok', tekst: wynik.komunikat || 'Zalogowano do portalu SFD.' });
      } else {
        setKomunikat({ typ: 'blad', tekst: wynik?.komunikat || 'Logowanie nie powiodło się.' });
      }
    } catch (err) {
      setKomunikat({ typ: 'blad', tekst: komunikatBledu(err, 'Test logowania nie powiódł się') });
    } finally {
      setTest(false);
    }
  };

  const testujPocztePrzycisk = async () => {
    setTestMail(true);
    setKomunikat(null);
    try {
      const wynik = await kontrolaFakturService.testujPoczte();
      if (wynik?.ok) {
        const numery = Array.isArray(wynik.znalezione_numery) ? wynik.znalezione_numery : [];
        setKomunikat({
          typ: 'ok',
          tekst: `Połączono${wynik.folder ? ` z folderem ${wynik.folder}` : ''}, wiadomości: ${wynik.wiadomosci ?? 0}`
            + (numery.length ? `, rozpoznane numery: ${numery.join(', ')}` : ', rozpoznanych numerów: brak'),
        });
      } else {
        setKomunikat({ typ: 'blad', tekst: wynik?.komunikat || 'Nie udało się połączyć ze skrzynką.' });
      }
    } catch (err) {
      setKomunikat({ typ: 'blad', tekst: komunikatBledu(err, 'Test poczty nie powiódł się') });
    } finally {
      setTestMail(false);
    }
  };

  if (ladowanie) {
    return <div style={{ padding: '2rem', textAlign: 'center', fontSize: '12px', color: 'var(--text-muted)' }}>Ładowanie…</div>;
  }

  const zajety = zapis || test || testMail;
  const sklepy = Array.isArray(ustawienia.sklepy) ? ustawienia.sklepy : [];
  const wierszeMapy = Array.isArray(ustawienia.mapa_sklepow) ? ustawienia.mapa_sklepow : [];
  const th = { padding: '5px 8px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
  const td = { padding: '4px 8px', borderBottom: '1px solid var(--border)', fontSize: '11px', verticalAlign: 'middle' };

  return (
    <div style={{ padding: '1.5rem', fontSize: '12px' }}>
      <h2 style={{ marginBottom: '4px', fontSize: '16px', fontWeight: 600 }}>SFD B2B — portal b2b.sfd.pl</h2>
      <p style={{ margin: '0 0 1rem', color: 'var(--text-muted)' }}>
        Dane logowania do portalu SFD, z którego <Link to="/admin?tab=kontrola-faktur" style={{ color: 'var(--primary)' }}>Kontrola faktur SFD</Link> pobiera listę faktur (tylko odczyt).
        Hasło jest szyfrowane i nigdy nie wraca do przeglądarki.
      </p>

      {komunikat && (
        <div data-testid="komunikat" style={{
          padding: '8px 12px', marginBottom: '1rem', borderRadius: '4px',
          backgroundColor: komunikat.typ === 'ok' ? 'var(--success-light)' : 'var(--danger-light)',
          color: komunikat.typ === 'ok' ? 'var(--success)' : 'var(--danger)',
        }}>
          {komunikat.tekst}
        </div>
      )}

      <div style={{
        backgroundColor: ustawienia.wlaczone ? 'var(--success-light)' : 'var(--bg)',
        border: `1px solid ${ustawienia.wlaczone ? 'var(--success)' : 'var(--border)'}`,
        borderRadius: '6px', padding: '12px', marginBottom: '1rem',
      }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={Boolean(ustawienia.wlaczone)}
            onChange={(e) => setUstawienia(prev => ({ ...prev, wlaczone: e.target.checked }))}
            style={{ width: '18px', height: '18px' }}
          />
          <span style={{ fontWeight: 600 }}>{ustawienia.wlaczone ? 'Integracja SFD B2B włączona' : 'Integracja SFD B2B wyłączona'}</span>
        </label>
      </div>

      <div style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '6px', padding: '12px', marginBottom: '1rem', display: 'grid', gap: '10px' }}>
        <div>
          <label htmlFor="sfd-email" style={label}>E-mail (login do portalu)</label>
          <input
            id="sfd-email"
            type="email"
            value={ustawienia.email || ''}
            onChange={(e) => setUstawienia(prev => ({ ...prev, email: e.target.value }))}
            placeholder="np. sklep@firma.pl"
            autoComplete="off"
            style={input}
          />
        </div>
        <div>
          <label htmlFor="sfd-haslo" style={label}>Hasło</label>
          <input
            id="sfd-haslo"
            type="password"
            value={haslo}
            onChange={(e) => setHaslo(e.target.value)}
            placeholder={ustawienia.ma_haslo ? '(bez zmian)' : 'wpisz hasło do portalu'}
            autoComplete="new-password"
            style={input}
          />
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
            {ustawienia.ma_haslo ? 'Hasło jest zapisane. Zostaw puste, żeby go nie zmieniać.' : 'Hasło nie jest jeszcze zapisane.'}
          </div>
        </div>
        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
          Ostatnia synchronizacja: {ustawienia.ostatnia_sync ? formatujDateCzas(ustawienia.ostatnia_sync) : 'nigdy'}
        </div>
      </div>

      {/* Mapowanie adresow dostawy -> sklepy */}
      <div data-testid="mapa-sklepow" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '6px', padding: '12px', marginBottom: '1rem' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: 600 }}>Mapowanie adresów dostawy na sklepy</h3>
        <p style={{ margin: '0 0 8px', fontSize: '11px', color: 'var(--text-muted)' }}>
          Portal SFD podaje adres dostawy każdej faktury. Adresy rozpoznane z już dodanych faktur mają źródło „nauka”; ręczne przypisanie („mapa”) ma pierwszeństwo.
          Adresy bez sklepu są wyróżnione — przypisz im sklep i kliknij „Zapisz”.
        </p>
        {wierszeMapy.length === 0 ? (
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Brak adresów — pojawią się po pierwszej synchronizacji z pobraniem nagłówków.</div>
        ) : (
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Adres (przykład)</th>
                  <th style={{ ...th, textAlign: 'right' }}>Dokumentów</th>
                  <th style={th}>Źródło</th>
                  <th style={th}>Sklep</th>
                </tr>
              </thead>
              <tbody>
                {wierszeMapy.map(w => {
                  const wybrany = mapa[w.klucz] === undefined ? (w.sklep_id ?? null) : mapa[w.klucz];
                  const nieznany = wybrany === null;
                  return (
                    <tr key={w.klucz} data-testid={`mapa-${w.klucz}`} style={{ backgroundColor: nieznany ? 'var(--warning-light)' : 'transparent' }}>
                      <td style={{ ...td, fontWeight: 600, color: nieznany ? 'var(--warning)' : 'var(--text)' }} title={`klucz: ${w.klucz}`}>{w.adres_przyklad || w.klucz}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{w.dokumentow ?? '—'}</td>
                      <td style={{ ...td, color: 'var(--text-muted)' }}>{w.zrodlo === 'mapa' ? 'mapa (ręcznie)' : w.zrodlo === 'nauka' ? 'nauka (z faktur)' : '—'}</td>
                      <td style={td}>
                        <select
                          aria-label={`Sklep dla ${w.adres_przyklad || w.klucz}`}
                          value={wybrany === null ? '' : String(wybrany)}
                          onChange={(e) => setMapa(prev => ({ ...prev, [w.klucz]: e.target.value === '' ? null : Number(e.target.value) }))}
                          style={{ padding: '4px 8px', fontSize: '11px', border: '1px solid var(--border)', borderRadius: '4px', backgroundColor: 'var(--card-bg)', color: nieznany ? 'var(--warning)' : 'var(--text)', minWidth: '180px' }}
                        >
                          <option value="">— nie przypisuj —</option>
                          {sklepy.map(s => <option key={s.id} value={String(s.id)}>{s.nazwa}</option>)}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Poczta jako zapasowe zrodlo plikow (ANEKS 4) */}
      <div data-testid="sekcja-poczty" style={{
        backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '6px',
        padding: '12px', marginBottom: '1rem',
      }}>
        <h3 style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: 600 }}>Poczta (zapasowe źródło plików)</h3>
        <p style={{ margin: '0 0 8px', fontSize: '11px', color: 'var(--text-muted)' }}>
          Gdy portal nie ma paczki dla dokumentu (WZ, korekty, starsze faktury), system poszuka jej w załącznikach skrzynki.
          Dla Gmaila użyj hasła aplikacji. Poczta czytana tylko do odczytu, nic nie jest oznaczane ani kasowane.
        </p>

        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '10px' }}>
          <input
            type="checkbox"
            checked={Boolean(ustawienia.sfd_mail_enabled)}
            onChange={(e) => setUstawienia(prev => ({ ...prev, sfd_mail_enabled: e.target.checked }))}
            style={{ width: '18px', height: '18px' }}
          />
          <span style={{ fontWeight: 600 }}>
            {ustawienia.sfd_mail_enabled ? 'Szukanie plików w poczcie włączone' : 'Szukanie plików w poczcie wyłączone'}
          </span>
        </label>

        <div style={{ display: 'grid', gap: '10px', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
          <div>
            <label htmlFor="mail-host" style={label}>Serwer IMAP</label>
            <input id="mail-host" type="text" value={ustawienia.sfd_mail_host || ''} autoComplete="off"
              onChange={(e) => setUstawienia(prev => ({ ...prev, sfd_mail_host: e.target.value }))}
              placeholder="imap.gmail.com" style={input} />
          </div>
          <div>
            <label htmlFor="mail-port" style={label}>Port</label>
            <input id="mail-port" type="number" value={ustawienia.sfd_mail_port ?? ''} min="1" max="65535"
              onChange={(e) => setUstawienia(prev => ({ ...prev, sfd_mail_port: e.target.value }))}
              placeholder="993" style={input} />
          </div>
          <div>
            <label htmlFor="mail-user" style={label}>Login (adres e-mail)</label>
            <input id="mail-user" type="text" value={ustawienia.sfd_mail_user || ''} autoComplete="off"
              onChange={(e) => setUstawienia(prev => ({ ...prev, sfd_mail_user: e.target.value }))}
              placeholder="np. faktury@firma.pl" style={input} />
          </div>
          <div>
            <label htmlFor="mail-haslo" style={label}>Hasło skrzynki</label>
            <input id="mail-haslo" type="password" value={hasloMail} autoComplete="new-password"
              onChange={(e) => setHasloMail(e.target.value)}
              placeholder={ustawienia.ma_haslo_mail ? '(bez zmian)' : 'hasło aplikacji (Gmail) lub hasło skrzynki'}
              style={input} />
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
              {ustawienia.ma_haslo_mail ? 'Hasło jest zapisane. Zostaw puste, żeby go nie zmieniać.' : 'Hasło nie jest jeszcze zapisane.'}
            </div>
          </div>
          <div>
            <label htmlFor="mail-folder" style={label}>Folder</label>
            <input id="mail-folder" type="text" value={ustawienia.sfd_mail_folder || ''} autoComplete="off"
              onChange={(e) => setUstawienia(prev => ({ ...prev, sfd_mail_folder: e.target.value }))}
              placeholder="INBOX" style={input} />
          </div>
          <div>
            <label htmlFor="mail-nadawcy" style={label}>Nadawcy (po przecinku)</label>
            <input id="mail-nadawcy" type="text" value={ustawienia.sfd_mail_nadawcy || ''} autoComplete="off"
              onChange={(e) => setUstawienia(prev => ({ ...prev, sfd_mail_nadawcy: e.target.value }))}
              placeholder="sfd.pl" style={input} />
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
              Dopasowanie „zawiera”; puste = bez filtra nadawcy.
            </div>
          </div>
          <div>
            <label htmlFor="mail-dni" style={label}>Ile dni wstecz</label>
            <input id="mail-dni" type="number" value={ustawienia.sfd_mail_dni ?? ''} min="1" max="3650"
              onChange={(e) => setUstawienia(prev => ({ ...prev, sfd_mail_dni: e.target.value }))}
              placeholder="180" style={input} />
          </div>
        </div>

        <div style={{ marginTop: '10px' }}>
          <button type="button" onClick={testujPocztePrzycisk} disabled={zajety} data-testid="btn-testuj-poczte"
            style={{ ...btn('var(--card-bg)', 'var(--primary)', 'var(--primary)'), opacity: zajety ? 0.6 : 1 }}>
            {testMail ? 'Sprawdzam pocztę…' : 'Testuj pocztę'}
          </button>
        </div>
      </div>

      {/* Skrypt Gmail - wplyw plikow bez hasla do skrzynki (ANEKS 5) */}
      <SfdWplywGmail />

      <div style={{ display: 'flex', gap: '8px' }}>
        <button type="button" onClick={testuj} disabled={zajety} style={{ ...btn('var(--card-bg)', 'var(--primary)', 'var(--primary)'), opacity: zajety ? 0.6 : 1 }}>
          {test ? 'Testuję…' : 'Testuj logowanie'}
        </button>
        <button type="button" onClick={zapisz} disabled={zajety} style={{ ...btn('var(--primary)', '#fff', 'var(--primary)'), opacity: zajety ? 0.6 : 1 }}>
          {zapis ? 'Zapisuję…' : 'Zapisz'}
        </button>
      </div>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>
        „Testuj logowanie” używa danych już zapisanych na serwerze — po zmianie e-maila/hasła najpierw kliknij „Zapisz”.
      </div>
    </div>
  );
};

export default SfdB2BSettings;
